import React, { useState, useEffect, useCallback } from 'react'
import styled from 'styled-components'
import format from 'date-fns/format'

import { MediaVersionsTable } from './MediaVersionsTable'
import { IconLarge } from '.'
import { faInfoCircle } from '@fortawesome/pro-light-svg-icons/faInfoCircle'
import { faTags } from '@fortawesome/pro-light-svg-icons/faTags'

export type Media = {
  id: string
  title: string
  description: string
  orientation: string
  copyright: string

  thumbnail: string
  assetType: ReadonlyArray<string>
  tags: ReadonlyArray<string>

  datePublished: string
  dateModified: string
  dateCreated: string

  files: ReadonlyArray<{
    id: string
    name: string
    fileName: string
    extension: string
    width: number
    height: number
    fileSize: number
  }>
}

export const MediaDetailOverlay: React.FC<{ apiHost: string; mediaId: string; onClose: () => void }> = ({
  apiHost,
  mediaId,
  onClose
}) => {
  const [status, setStatus] = useState<'initial' | 'loading' | 'error' | 'ok'>('initial')
  const [media, setMedia] = useState<undefined | Media>(undefined)

  const closeOnEsc = useCallback((e: KeyboardEvent) => e.keyCode === 27 && onClose(), [onClose])
  useEffect(() => {
    document.addEventListener('keydown', closeOnEsc) // Close when esc is pressed
    document.body.style.overflow = 'hidden' // Disable scrolling

    // Get media details
    const abortController = new AbortController()
    const getDetails = async () => {
      setStatus('loading')
      try {
        const data = await fetch(`${apiHost}/Asset/${mediaId}`, { signal: abortController.signal })
        const media: Media = await data.json()
        setMedia(media)
        setStatus('ok')
      } catch (error) {
        console.error(error)
        setStatus('error')
      }
    }

    getDetails()

    return () => {
      abortController.abort()
      document.removeEventListener('keydown', closeOnEsc)
      document.body.style.overflow = 'unset'
    }
  }, [mediaId, apiHost, closeOnEsc])

  if (status === 'initial' || status === 'loading') {
    return (
      <Overlay>
        <TopRow>
          <div>
            <Title>-</Title>
            <Description>-</Description>
          </div>

          <CloseButton onClick={onClose} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M23.5 23.999C23.366 23.999 23.241 23.947 23.146 23.853L12 12.706L0.854006 23.853C0.760006 23.947 0.634006 23.999 0.500006 23.999C0.366006 23.999 0.241006 23.947 0.146006 23.853C-0.0489941 23.658 -0.0489941 23.341 0.146006 23.146L11.292 12L0.147006 0.852993C-0.0479941 0.657993 -0.0479941 0.340993 0.147006 0.145993C0.241006 0.0509929 0.367006 -0.00100708 0.500006 -0.00100708C0.633006 -0.00100708 0.759006 0.0509929 0.854006 0.144993L12 11.292L23.147 0.145993C23.241 0.0519929 23.367 -7.08003e-06 23.501 -7.08003e-06C23.635 -7.08003e-06 23.76 0.0519929 23.855 0.145993C24.05 0.340993 24.05 0.657993 23.855 0.852993L12.707 11.999L23.853 23.145C24.048 23.34 24.048 23.657 23.853 23.852C23.759 23.947 23.634 23.999 23.5 23.999Z"
              fill="#303030"
            />
          </CloseButton>
        </TopRow>

        <Row>
          <div>
            <ImageContainer />
            <MediaVersionsTable apiHost={apiHost} mediaId={mediaId} files={[]} />
          </div>

          <Details>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
              <IconLarge icon={faInfoCircle} />
              <PLarge style={{ marginLeft: 8 }}>Information</PLarge>
            </div>
            <Info title={'Created'} values={['-']} />
            <Info title={'Published'} values={['-']} />
            <Info title={'Modified'} values={['-']} />

            <div style={{ margin: '16px 0 8px 0', display: 'flex', alignItems: 'center' }}>
              <IconLarge icon={faTags} />
              <PLarge style={{ marginLeft: 8 }}>Tags</PLarge>
            </div>
          </Details>
        </Row>
      </Overlay>
    )
  }

  if (status === 'error') {
    return (
      <Overlay>
        <TopRow>
          <div />
          <CloseButton onClick={onClose} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M23.5 23.999C23.366 23.999 23.241 23.947 23.146 23.853L12 12.706L0.854006 23.853C0.760006 23.947 0.634006 23.999 0.500006 23.999C0.366006 23.999 0.241006 23.947 0.146006 23.853C-0.0489941 23.658 -0.0489941 23.341 0.146006 23.146L11.292 12L0.147006 0.852993C-0.0479941 0.657993 -0.0479941 0.340993 0.147006 0.145993C0.241006 0.0509929 0.367006 -0.00100708 0.500006 -0.00100708C0.633006 -0.00100708 0.759006 0.0509929 0.854006 0.144993L12 11.292L23.147 0.145993C23.241 0.0519929 23.367 -7.08003e-06 23.501 -7.08003e-06C23.635 -7.08003e-06 23.76 0.0519929 23.855 0.145993C24.05 0.340993 24.05 0.657993 23.855 0.852993L12.707 11.999L23.853 23.145C24.048 23.34 24.048 23.657 23.853 23.852C23.759 23.947 23.634 23.999 23.5 23.999Z"
              fill="#303030"
            />
          </CloseButton>
        </TopRow>

        <Description>Something went wrong when getting the asset from our server. Please try again later.</Description>
      </Overlay>
    )
  }

  return (
    <Overlay>
      <TopRow>
        <div>
          <Title>{media?.title}</Title>
          <Description>{media?.description}</Description>
        </div>

        <CloseButton onClick={onClose} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M23.5 23.999C23.366 23.999 23.241 23.947 23.146 23.853L12 12.706L0.854006 23.853C0.760006 23.947 0.634006 23.999 0.500006 23.999C0.366006 23.999 0.241006 23.947 0.146006 23.853C-0.0489941 23.658 -0.0489941 23.341 0.146006 23.146L11.292 12L0.147006 0.852993C-0.0479941 0.657993 -0.0479941 0.340993 0.147006 0.145993C0.241006 0.0509929 0.367006 -0.00100708 0.500006 -0.00100708C0.633006 -0.00100708 0.759006 0.0509929 0.854006 0.144993L12 11.292L23.147 0.145993C23.241 0.0519929 23.367 -7.08003e-06 23.501 -7.08003e-06C23.635 -7.08003e-06 23.76 0.0519929 23.855 0.145993C24.05 0.340993 24.05 0.657993 23.855 0.852993L12.707 11.999L23.853 23.145C24.048 23.34 24.048 23.657 23.853 23.852C23.759 23.947 23.634 23.999 23.5 23.999Z"
            fill="#303030"
          />
        </CloseButton>
      </TopRow>

      <Row>
        <div>
          <ImageContainer>
            <Img src={media?.thumbnail} alt={media?.title} />
          </ImageContainer>

          <MediaVersionsTable apiHost={apiHost} mediaId={mediaId} files={media?.files!} />
        </div>

        <Details>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
            <IconLarge icon={faInfoCircle} />
            <PLarge style={{ marginLeft: 8 }}>Information</PLarge>
          </div>

          {media?.dateCreated !== '' && (
            <Info title={'Created'} values={[format(new Date(media?.dateCreated!), `d MMMM yyyy`)]} />
          )}

          {media?.datePublished !== '' && (
            <Info title={'Published'} values={[format(new Date(media?.datePublished!), `d MMMM yyyy`)]} />
          )}

          {media?.dateModified !== '' && (
            <Info title={'Modified'} values={[format(new Date(media?.dateModified!), `d MMMM yyyy`)]} />
          )}

          {media?.copyright !== '' && <Info title={'Copyright'} values={[media?.copyright!]} />}
          {media?.assetType.length !== 0 && <Info title={'Type'} values={media?.assetType ?? []} />}
          {media?.orientation !== '' && <Info title={'Orientation'} values={[media?.orientation!]} />}

          <div style={{ margin: '16px 0 8px 0', display: 'flex', alignItems: 'center' }}>
            <IconLarge icon={faTags} />
            <PLarge style={{ marginLeft: 8 }}>Tags</PLarge>
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap' }}>
            {media?.tags.map((tag) => (
              <Tag key={tag}>{tag}</Tag>
            ))}
          </div>
        </Details>
      </Row>
    </Overlay>
  )
}

const Info: React.FC<{ title: string; values: ReadonlyArray<string> }> = ({ title, values }) => (
  <div style={{ marginBottom: 8 }}>
    <P>{title}</P>
    {values.map((value) => (
      <PSecondary key={value}>{value}</PSecondary>
    ))}
  </div>
)

const Overlay = styled.main`
  overflow-y: scroll;
  position: fixed;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;

  width: 100%;
  min-height: 100vh;
  background: white;

  padding: 40px 48px;
`

const Row = styled.div`
  display: flex;
  align-items: flex-start;

  & > *:first-child {
    margin-right: 24px;
    flex: 0 0 calc(70% - 24px);
  }

  & > *:last-child {
    flex: 0 0 30%;
  }
`

const Details = styled.div`
  border-radius: 4px;
  padding: 24px;
  background: #f5f5f5;
`

const TopRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 24px;
`

const Title = styled.h1`
  font-family: Roboto Condensed;
  font-weight: bold;
  font-size: 25px;
  line-height: 25px;
  transform: uppercase;
  color: #303030;
  margin-bottom: 8px;
`

const P = styled.p`
  margin: 0;
  font-family: Roboto;
  font-size: 12px;
  line-height: 19px;
  color: #303030;
`

const PLarge = styled(P)`
  font-size: 14px;
  line-height: 22px;
`

const PSecondary = styled(P)`
  color: #a2a2a2;
`

const Description = styled(PLarge)`
  color: #a2a2a2;
`

const CloseButton = styled.svg`
  width: 24px;
  height: 24px;
  cursor: pointer;
  path {
    transition: fill 150ms cubic-bezier(0.33, 1, 0.68, 1);
  }
  &:hover path {
    fill: #a2a2a2;
  }
`

const ImageContainer = styled.div`
  width: 100%;
  height: 550px;
  margin-bottom: 24px;

  display: flex;
  align-items: center;
  justify-content: center;

  background-image: linear-gradient(45deg, #d6d6d6 25%, transparent 25%),
    linear-gradient(-45deg, #d6d6d6 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #d6d6d6 75%),
    linear-gradient(-45deg, transparent 75%, #d6d6d6 75%);
  background-size: 22px 22px;
  background-position: 0 0, 0 11px, 11px -11px, -11px 0px;
`

const Img = styled.img`
  max-height: 550px;
  object-fit: contain;
`

const Tag = styled.div`
  font-family: Roboto;
  font-weight: 500;
  color: #303030;
  font-size: 10px;
  line-height: 12px;

  margin-right: 8px;
  margin-bottom: 8px;
  padding: 6px 10px;
  background: #ffffff;
  box-shadow: 0px 1px 3px rgba(236, 236, 236, 0.56);
  border-radius: 12px;
`
